import { useState } from "react";
import { CalculatorPage } from "../../components/CalculatorPage";
import { ResultExplainer } from "../../components/ResultExplainer";
import { FilingStatusSelector } from "../../components/FilingStatusSelector";
import { ComparisonCard } from "../../components/ComparisonCard";
import { taxAPI, formatCurrency } from "../../lib/api";
import { buildCalculatorSchema } from "../../hooks/useSEO";
import { ComparisonChart } from "../../components/ComparisonChart";
import { EmailGatePdfButton } from "../../components/EmailGatePdfButton";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const CapitalGainsCalculator = () => {
  const [filing, setFiling] = useState("single");
  const [ltcg, setLtcg] = useState("50000");
  const [ordinary, setOrdinary] = useState("100000");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await taxAPI.capitalGains({
        long_term_gain: Number(ltcg),
        ordinary_income: Number(ordinary),
        filing_status: filing,
      });
      setResult(res);
    } catch (e) {
      toast.error("Calculation failed.");
    } finally {
      setLoading(false);
    }
  };

  const inputPanel = (
    <div className="space-y-5">
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Filing Status</label>
        <FilingStatusSelector value={filing} onChange={setFiling} testid="ltcg-filing" />
      </div>
      <div>
        <label htmlFor="ltcg" className="font-body text-sm font-semibold block mb-2">Long-Term Capital Gain (USD)</label>
        <input
          id="ltcg"
          type="number"
          inputMode="decimal"
          value={ltcg}
          onChange={(e) => setLtcg(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-ltcg"
        />
      </div>
      <div>
        <label htmlFor="ord" className="font-body text-sm font-semibold block mb-2">Ordinary Income (USD)</label>
        <input
          id="ord"
          type="number"
          inputMode="decimal"
          value={ordinary}
          onChange={(e) => setOrdinary(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-ordinary"
        />
      </div>
      <button
        onClick={run}
        disabled={loading}
        data-testid="calculate-btn"
        className="w-full bg-primary text-primary-foreground font-mono uppercase tracking-widest text-sm px-6 py-4 mt-3 hover:brightness-110 transition flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Calculating…</> : "Calculate LTCG"}
      </button>
    </div>
  );

  const resultPanel = result ? (
    <div className="space-y-6" data-testid="ltcg-result">
      <ComparisonCard
        testid="ltcg-comparison"
        rows={[
          { label: "LTCG Tax", value2025: result.year_2025.ltcg_tax, value2026: result.year_2026.ltcg_tax, format: "currency" },
          { label: "Net Investment Income Tax (3.8%)", value2025: result.year_2025.niit, value2026: result.year_2026.niit, format: "currency" },
        ]}
        totals={{
          label: "Total Tax on Gain",
          value2025: result.year_2025.total_tax_on_gain,
          value2026: result.year_2026.total_tax_on_gain,
          format: "currency",
        }}
      />
      <div className="bg-muted border border-foreground/15 p-5">
        <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-1 block">Note</span>
        <p className="font-body text-sm">{result.note}</p>
      </div>

      <ComparisonChart
        testid="ltcg-chart"
        title="Capital gains tax: 2025 vs 2026"
        rows={[
          { label: "LTCG Tax", v2025: result.year_2025.ltcg_tax, v2026: result.year_2026.ltcg_tax },
          { label: "NIIT (3.8%)", v2025: result.year_2025.niit, v2026: result.year_2026.niit },
          { label: "Total", v2025: result.year_2025.total_tax_on_gain, v2026: result.year_2026.total_tax_on_gain },
        ]}
      />

      <EmailGatePdfButton
        testid="ltcg-pdf"
        filename="taxshift-2026-capital-gains.pdf"
        pdfPayload={{
          title: "2026 Capital Gains Analysis",
          subtitle: `${result.filing_status.replace("_", " ").toUpperCase()} · LTCG comparison`,
          inputs: [
            { label: "Long-Term Gain", value: formatCurrency(result.long_term_gain) },
            { label: "Ordinary Income", value: formatCurrency(result.ordinary_income) },
            { label: "Filing Status", value: result.filing_status.replace("_", " ").toUpperCase() },
          ],
          headline: `Total tax on your gain: ${formatCurrency(result.year_2025.total_tax_on_gain)} in 2025 vs ${formatCurrency(result.year_2026.total_tax_on_gain)} in 2026.`,
          rows: [
            { label: "LTCG Tax", v2025: formatCurrency(result.year_2025.ltcg_tax), v2026: formatCurrency(result.year_2026.ltcg_tax) },
            { label: "NIIT", v2025: formatCurrency(result.year_2025.niit), v2026: formatCurrency(result.year_2026.niit) },
          ],
          totals: {
            label: "Total Tax on Gain",
            v2025: formatCurrency(result.year_2025.total_tax_on_gain),
            v2026: formatCurrency(result.year_2026.total_tax_on_gain),
          },
        }}
      />
      <ResultExplainer slug="capital-gains-2026" />
    </div>
  ) : (
    <div className="border-2 border-dashed border-foreground/30 p-12 text-center bg-card/50" data-testid="ltcg-empty">
      <p className="font-heading text-xl">Enter your gains and income, then click Calculate.</p>
    </div>
  );

  return (
    <CalculatorPage
      title="2026 Capital Gains Tax Calculator"
      slug="capital-gains-2026"
      overline="Calculator · Investments"
      description="Model long-term capital gains tax under 2025 and 2026 thresholds. The 0%, 15%, and 20% LTCG rates stay the same, but bracket thresholds shift and ordinary-income bracket changes affect stacking."
      seoTitle="2026 Long-Term Capital Gains Calculator — OBBBA Era"
      seoKeywords="capital gains tax 2026, ltcg calculator, niit 2026, long term capital gains 2026"
      jsonLd={buildCalculatorSchema({
        name: "2026 Capital Gains Calculator",
        description: "Free calculator for 2026 long-term capital gains tax.",
        url: "/calculator/capital-gains-2026",
      })}
      inputPanel={inputPanel}
      resultPanel={resultPanel}
      education={
        <>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-6">
            Why LTCG rates barely budge
          </h2>
          <div className="font-body text-base leading-relaxed space-y-4 text-foreground/85">
            <p>
              The TCJA did not materially change long-term capital gains (LTCG) rates —
              they remained 0%, 15%, and 20%. It did, however, decouple the LTCG thresholds from
              ordinary income brackets, setting them independently.
            </p>
            <p>
              In 2026, the LTCG thresholds continue to be separately inflation-indexed, meaning
              your LTCG tax on a given gain stays roughly constant. What can change is the{" "}
              <span className="font-semibold">Net Investment Income Tax</span> (3.8%) exposure
              based on your total MAGI — and whether your ordinary income pushes gains into a
              higher LTCG bracket.
            </p>
          </div>
        </>
      }
      relatedSlugs={[
        { slug: "tax-bracket-2026", label: "Tax Bracket" },
        { slug: "qbi-deduction-2026", label: "QBI Deduction" },
      ]}
    />
  );
};

export default CapitalGainsCalculator;
